import { AlertComponent } from 'Components/HelperComponents'
import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { useEdition } from 'hooks/useEdition'
import { useGuillotina } from 'hooks/useGuillotina'
import { AddAccreditationsForm } from 'NewVersion/components/Forms/AddAccreditationsForm'
import { ModalInfo } from 'NewVersion/components/UI/Modals/Modal'
import { AddProfessionalsIconOutline } from 'NewVersion/icons/AddProfessionalsIconOutline'
import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { ENS_TYPES_OBJ, getEnsType } from 'utils/entityUtils'

const PerformingEntityAccreditationsForm = ({ entityId, onSubmit, onWarningCancel }) => {
  const { t } = useTranslation()
  const { edition, currentEdition } = useEdition()
  const {
    data: entity,
    isLoading: loadingEntity,
    error: entityError,
  } = useGuillotina({
    path: entityId ? `${edition}/${entityId}` : null,
  })
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [hasAcceptedPaidAccreditations, setHasAcceptedPaidAccreditations] = useState(false)
  const ensType = getEnsType(entity?.type_name, entity?.tipus)
  const ensTypeObj = ensType ? ENS_TYPES_OBJ[ensType] : null

  const totalIncludedAccreditations = useMemo(() => {
    if (!currentEdition || !ensTypeObj?.totalAccreditationAttribute) {
      return null
    }

    const total = parseInt(
      currentEdition.getConfigData()?.[ensTypeObj.totalAccreditationAttribute],
      10
    )

    return Number.isFinite(total) ? total : null
  }, [currentEdition, ensTypeObj])

  const isTotalAccreditationsFilled = useMemo(() => {
    if (!entity || !Number.isFinite(totalIncludedAccreditations)) {
      return false
    }

    return Number(entity.total_acreditats || 0) >= totalIncludedAccreditations
  }, [entity, totalIncludedAccreditations])

  useEffect(() => {
    setShowWarningModal(isTotalAccreditationsFilled)
    setHasAcceptedPaidAccreditations(false)
  }, [entityId, isTotalAccreditationsFilled])

  if (entityError) {
    return <AlertComponent type="danger">{t('error_generic')}</AlertComponent>
  }

  if (loadingEntity || !entity) {
    return <LoadingComponent />
  }

  return (
    <>
      {Number.isFinite(totalIncludedAccreditations) && (
        <div data-test="performing-accreditations-info">
          <AlertComponent type="info" className="performing-accreditations-info">
            <Trans
              i18nKey="performing_accreditations_info"
              values={{
                included: totalIncludedAccreditations,
              }}
              components={{ strong: <strong /> }}
            />
            <br />
            <Trans
              i18nKey="performing_accreditations_info_2"
              values={{
                price: currentEdition?.getConfigData()?.preu_acreditacio,
              }}
              components={{ strong: <strong /> }}
            />
          </AlertComponent>
        </div>
      )}
      {hasAcceptedPaidAccreditations && (
        <AlertComponent type="warning" className="paid-accreditations-notice">
          <div className="paid-accreditations-notice__title">
            {t('paid_accreditations_notice_title', 'Atenció')}
          </div>
          <div className="paid-accreditations-notice__text">
            <Trans
              i18nKey="paid_accreditations_notice"
              values={{ price: currentEdition?.getConfigData()?.preu_acreditacio }}
              components={{ strong: <strong /> }}
            />
          </div>
        </AlertComponent>
      )}
      <AddAccreditationsForm
        ensType={ensType}
        entity={entity}
        loadingEntity={loadingEntity}
        submitAction={onSubmit}
        showPaidAccreditationsWarning={true}
        hasAcceptedPaidAccreditations={hasAcceptedPaidAccreditations}
        onPaidAccreditationsAccepted={() => setHasAcceptedPaidAccreditations(true)}
      />
      <ModalInfo
        open={showWarningModal}
        setOpen={setShowWarningModal}
        text={
          <Trans
            i18nKey="alert_professional_acreditat"
            values={{ price: currentEdition?.getConfigData()?.preu_acreditacio }}
          />
        }
        icon={<AddProfessionalsIconOutline fill={'#273BF3'} />}
        handleOk={() => {
          setHasAcceptedPaidAccreditations(true)
          setShowWarningModal(false)
        }}
        handleCancel={() => {
          if (onWarningCancel) {
            onWarningCancel()
          }
        }}
        closeAction={onWarningCancel}
        textOk={t('alert_professional_acreditat_ok')}
        textCancel={t('alert_professional_acreditat_cancel')}
      />
    </>
  )
}

export default PerformingEntityAccreditationsForm
